import React from "react";
import { Card, Input, Select, Divider, Typography, DatePicker } from "antd";
import { foldSearch } from "../../../../utils/searchFold";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Sentinel option that switches the region field from "pick an existing one"
// to "type a new one". Kept out of the region namespace so it can't collide
// with a real region name.
const NEW_REGION = "__new_region__";

const Step1 = ({
  selectedWorkspace,
  workspaceOptions,
  handleWorkspaceChange,
  regionMode,
  handleRegionModeChange,
  selectedRegion,
  handleSelectedRegionChange,
  regionOptions,
  regionName,
  handleRegionNameChange,
  newMarket,
  handleNewMarketChange,
  availableMarketOptions,
  existingMarkets,
  copyFromMarket,
  handleCopyFromMarketChange,
  copyFromMarketOptions,
  marketsLoading,
  contractMonthRange,
  handleContractMonthRangeChange,
}) => {
  return (
    <Card title="Select Workspace & Market">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
            Workspace *
          </label>
          <Select
            allowClear
            showSearch
            placeholder="Select an existing workspace"
            value={selectedWorkspace || undefined}
            onChange={handleWorkspaceChange}
            style={{ width: "100%" }}
            options={workspaceOptions}
            filterOption={(input, option) =>
              foldSearch(option?.label).includes(foldSearch(input))
            }
          />
        </div>

        {selectedWorkspace && (
          <Text
            type="secondary"
            style={{ fontSize: 12 }}
          >
            Existing markets:{" "}
            {existingMarkets.length
              ? existingMarkets.map((m) => m.toUpperCase()).join(", ")
              : "none"}
          </Text>
        )}

        <Divider
          orientation="left"
          style={{ margin: "8px 0", fontSize: 14, fontWeight: 500 }}
        >
          New Market
        </Divider>

        {!selectedWorkspace && (
          <Text type="secondary">Select a workspace first.</Text>
        )}

        {selectedWorkspace && (
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div>
              <label
                style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
              >
                Region *
              </label>
              <Select
                showSearch
                placeholder="Select a region"
                value={
                  regionMode === "new"
                    ? NEW_REGION
                    : selectedRegion || undefined
                }
                onChange={(val) =>
                  val === NEW_REGION
                    ? handleRegionModeChange("new")
                    : handleSelectedRegionChange(val)
                }
                style={{ width: "100%" }}
                options={[
                  ...regionOptions,
                  { value: NEW_REGION, label: "+ Create new region" },
                ]}
                filterOption={(input, option) =>
                  foldSearch(option?.label).includes(foldSearch(input))
                }
              />
              {regionMode === "new" && (
                <Input
                  allowClear
                  style={{ marginTop: 8 }}
                  value={regionName}
                  onChange={handleRegionNameChange}
                  placeholder="New region name, e.g. Europe"
                />
              )}
            </div>
            <div>
              <label
                style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
              >
                Market *
              </label>
              <Select
                allowClear
                showSearch
                loading={marketsLoading}
                placeholder={
                  marketsLoading ? "Loading markets..." : "Select market to add"
                }
                value={newMarket || undefined}
                onChange={handleNewMarketChange}
                style={{ width: "100%" }}
                options={availableMarketOptions}
                notFoundContent={
                  marketsLoading ? "Loading..." : "No new markets available"
                }
                filterOption={(input, option) =>
                  foldSearch(option?.label).includes(foldSearch(input))
                }
              />
            </div>
          </div>
        )}

        {selectedWorkspace && (
          <>
            <Divider
              orientation="left"
              style={{ margin: "8px 0", fontSize: 14, fontWeight: 500 }}
            >
              Configuration Settings
            </Divider>
            <div>
              <label
                style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
              >
                Copy configuration from
              </label>
              <Select
                allowClear
                showSearch
                disabled={!newMarket}
                placeholder={
                  newMarket
                    ? "Optional — reuse an existing market's setup"
                    : "Select a market to add first"
                }
                value={copyFromMarket || undefined}
                onChange={handleCopyFromMarketChange}
                style={{ width: "100%" }}
                options={copyFromMarketOptions}
                filterOption={(input, option) =>
                  foldSearch(option?.label).includes(foldSearch(input))
                }
              />
              <Text
                type="secondary"
                style={{ display: "block", marginTop: 4, fontSize: 12 }}
              >
                Fills the weights, company and core brands of the next two
                steps as a starting point. Everything stays editable.
              </Text>
            </div>
            <div>
              <label
                style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
              >
                Contract Month Range
              </label>
              <RangePicker
                picker="month"
                value={contractMonthRange}
                onChange={handleContractMonthRangeChange}
                style={{ width: "100%" }}
                placeholder={["Start Month", "End Month"]}
                format="YYYY-MM"
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default Step1;
